import React, { useEffect, useState } from "react";
import { Trophy } from "@phosphor-icons/react";
import api from "@/lib/api";

const MIN_VIEWS = 20;
const best = (rows) => [...(rows || [])].filter((r) => r.views >= MIN_VIEWS && r.call_rate != null)
  .sort((x, y) => y.call_rate - x.call_rate || y.views - x.views)[0];
const pct = (v) => (v == null ? "—" : `${v}%`);
// Layouts that can win first (most calls on top), then the ones still under the view minimum.
const ordered = (rows) => [...rows].sort((x, y) => (y.views >= MIN_VIEWS) - (x.views >= MIN_VIEWS) || (y.call_rate ?? -1) - (x.call_rate ?? -1));
const KEEP = "border-2 border-black bg-emerald-700 px-2 py-1 text-[10px] uppercase tracking-widest text-white inline-flex items-center gap-1 hover:bg-emerald-800 disabled:opacity-50";

function ResultsTable({ rows, current, win, p }) {
  if (!rows.length) return <p className="py-2 text-[11px] text-zinc-500" data-testid={`${p}-empty`}>No thank-you visits for this buyer in this period yet.</p>;
  return (
    <table className="w-full text-[11px]" data-testid={`${p}-table`}>
      <thead><tr className="border-b-2 border-black text-left label-mono">
        <th className="py-1 pr-2">Layout</th><th className="py-1 pr-2 text-right">Views</th><th className="py-1 pr-2 text-right">Call taps</th><th className="py-1 text-right">Call rate</th>
      </tr></thead>
      <tbody>
        {ordered(rows).map((r) => (
          <tr key={r.key} className={`border-b border-zinc-200 ${r.views < MIN_VIEWS ? "text-zinc-500" : ""}`} data-testid={`${p}-row-${r.key.replace(":", "-")}`}>
            <td className="py-1 pr-2 font-bold">
              {r.name}
              {r.key === current && <span className="ml-1.5 border border-black px-1 text-[9px] uppercase">Now using</span>}
              {win?.key === r.key && <span className="ml-1.5 bg-emerald-600 px-1 text-[9px] uppercase text-white">Most calls</span>}
              {r.views < MIN_VIEWS && <span className="ml-1.5 text-[9px] font-normal uppercase">under {MIN_VIEWS} views</span>}
            </td>
            <td className="py-1 pr-2 text-right tabular-nums">{r.views}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{r.call}</td>
            <td className="py-1 text-right tabular-nums">{pct(r.call_rate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Verdict({ win, allWin, current, busy, onKeep, p }) {
  if (win) {
    if (win.key === current) return <p className="text-[11px] text-emerald-800" data-testid={`${p}-verdict`}><b>{win.name}</b> already gets the most calls for this buyer ({pct(win.call_rate)}). Keep it.</p>;
    return (
      <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`${p}-verdict`}>
        <span><b>{win.name}</b> gets the most calls for this buyer ({pct(win.call_rate)} of visitors tap Call).</span>
        <button type="button" className={KEEP} disabled={busy} onClick={() => onKeep(win.key)} data-testid={`${p}-keep-winner`}><Trophy size={11} weight="bold" /> Keep {win.name}</button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5 text-[11px] text-zinc-700" data-testid={`${p}-verdict`}>
      <p>Not enough visits to pick a winner yet: a layout needs {MIN_VIEWS}+ thank-you views for this buyer. A buyer shows one layout at a time, so switch every few weeks, or use <b>Design test</b> on the buyer row to split visitors for you.</p>
      {allWin && (
        <div className="flex flex-wrap items-center gap-2">
          <span>Across all buyers, <b>{allWin.name}</b> gets the most calls ({pct(allWin.call_rate)}).</span>
          {allWin.key !== current && <button type="button" className={KEEP} disabled={busy} onClick={() => onKeep(allWin.key)} data-testid={`${p}-use-overall`}>Use {allWin.name}</button>}
        </div>
      )}
    </div>
  );
}

// Which layout gets the most call taps for this buyer, with one click to keep the winner.
export function LayoutResults({ b, saved, busy, keep }) {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const p = `ty-preview-${b.code}-results`;
  useEffect(() => {
    let live = true;
    setData(null);
    Promise.all([
      api.get("/admin/thankyou-stats", { params: { days, buyer: b.code } }),
      api.get("/admin/thankyou-stats", { params: { days } }),
    ]).then(([m, a]) => { if (live) setData({ mine: m.data.designs, all: a.data.designs, def: m.data.default }); })
      .catch(() => { if (live) setData(false); });
    return () => { live = false; };
  }, [b.code, days]);
  const current = saved || data?.def;
  return (
    <div className="mt-3 border-2 border-black p-2" data-testid={p}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="label-mono">Layout results · call taps on this buyer&apos;s thank-you page</span>
        <select className="ml-auto border-2 border-black bg-white px-1 py-0.5 text-[10px]" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid={`${p}-days`}>
          <option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
        </select>
      </div>
      {data === null && <div className="label-mono text-zinc-500">Loading…</div>}
      {data === false && <div className="text-[11px] text-red-700">Couldn&apos;t load the results.</div>}
      {data && (
        <div className="space-y-2">
          <ResultsTable rows={data.mine} current={current} win={best(data.mine)} p={p} />
          <Verdict win={best(data.mine)} allWin={best(data.all)} current={current} busy={busy} onKeep={keep} p={p} />
          <p className="text-[10px] text-zinc-500">Call rate = call taps per 100 thank-you page views. Staff previews aren&apos;t counted.</p>
        </div>
      )}
    </div>
  );
}
